import React, { useEffect, useMemo, useRef, useState } from "react";
import construction from "../../images/underconstruction.png";
import youtubelogo from "../../images/youtube.png";
import twitterlogo from "../../images/twitter.png";
import musiclogo from "../../images/music.png";
import styles from "./home.module.css";

import cube from "./cube.png";

// Explosion assets (same folder as this component)
import explosionSheet from "./PlayerExplosion_12-uhd.png";
import explodeSfx from "./explode.mp3"; // <-- replace with your file name

// ----- helpers -----
function parseBracedPair(str) {
  const m = String(str).match(/\{\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\}/);
  if (!m) return [0, 0];
  return [parseFloat(m[1]), parseFloat(m[2])];
}

function parseTextureRect(str) {
  const m = String(str).match(
    /\{\{\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\}\s*,\s*\{\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\}\s*\}\s*/
  );
  if (!m) return { x: 0, y: 0, w: 0, h: 0 };
  return { x: parseFloat(m[1]), y: parseFloat(m[2]), w: parseFloat(m[3]), h: parseFloat(m[4]) };
}

function parsePlist(plistText) {
  const doc = new DOMParser().parseFromString(plistText, "application/xml");
  const rootDict = doc.querySelector("plist > dict");
  if (!rootDict) throw new Error("Invalid plist");

  function findValueForKey(dictEl, keyName) {
    const kids = Array.from(dictEl.children);
    for (let i = 0; i < kids.length; i++) {
      if (kids[i].tagName === "key" && kids[i].textContent === keyName) {
        return kids[i + 1] || null;
      }
    }
    return null;
  }

  const framesDict = findValueForKey(rootDict, "frames");
  if (!framesDict || framesDict.tagName !== "dict") throw new Error("Missing frames dict");

  const framesKids = Array.from(framesDict.children);
  const frames = [];

  let sourceW = 978, sourceH = 1042;

  for (let i = 0; i < framesKids.length; i++) {
    const keyNode = framesKids[i];
    if (keyNode.tagName !== "key") continue;

    const name = keyNode.textContent || "";
    const frameDict = framesKids[i + 1];
    if (!frameDict || frameDict.tagName !== "dict") continue;

    const spriteOffsetEl = findValueForKey(frameDict, "spriteOffset");
    const spriteSizeEl = findValueForKey(frameDict, "spriteSize");
    const spriteSourceSizeEl = findValueForKey(frameDict, "spriteSourceSize");
    const textureRectEl = findValueForKey(frameDict, "textureRect");
    const textureRotatedEl = findValueForKey(frameDict, "textureRotated");

    const [offX, offY] = parseBracedPair(spriteOffsetEl?.textContent);
    const [sprW, sprH] = parseBracedPair(spriteSizeEl?.textContent);
    const [srcW, srcH] = parseBracedPair(spriteSourceSizeEl?.textContent);
    const rect = parseTextureRect(textureRectEl?.textContent);

    const rotated = textureRotatedEl?.tagName === "true";

    if (srcW && srcH) {
      sourceW = srcW;
      sourceH = srcH;
    }

    frames.push({
      name,
      offsetX: offX,
      offsetY: offY,
      spriteW: sprW,
      spriteH: sprH,
      sourceW: srcW || sourceW,
      sourceH: srcH || sourceH,
      rectX: rect.x,
      rectY: rect.y,
      rectW: rect.w,
      rectH: rect.h,
      rotated,
    });
  }

  frames.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  return { frames, sourceW, sourceH };
}

function drawFrame(ctx, sheetImg, frame, canvasW, canvasH, displayW, displayH) {
  ctx.clearRect(0, 0, canvasW, canvasH);

  const scale = Math.min(displayW / frame.sourceW, displayH / frame.sourceH);

  const boxX = (canvasW - frame.sourceW * scale) / 2;
  const boxY = (canvasH - frame.sourceH * scale) / 2;

  const srcCenterX = frame.sourceW / 2;
  const srcCenterY = frame.sourceH / 2;

  // offsetY in plist is +up, canvas Y is +down => subtract
  const drawXInSource = srcCenterX + frame.offsetX - frame.spriteW / 2;
  const drawYInSource = srcCenterY - frame.offsetY - frame.spriteH / 2;

  const dx = boxX + drawXInSource * scale;
  const dy = boxY + drawYInSource * scale;

  const dw = frame.spriteW * scale;
  const dh = frame.spriteH * scale;

  ctx.imageSmoothingEnabled = true;

  ctx.save();
  if (frame.rotated) {
    // Rotate atlas region back -90deg into (dw x dh)
    ctx.translate(dx + dw / 2, dy + dh / 2);
    ctx.rotate(-Math.PI / 2);

    // swapped sizes after rotation
    ctx.drawImage(
      sheetImg,
      frame.rectX,
      frame.rectY,
      frame.rectW,
      frame.rectH,
      -dh / 2,
      -dw / 2,
      dh,
      dw
    );
  } else {
    ctx.drawImage(
      sheetImg,
      frame.rectX,
      frame.rectY,
      frame.rectW,
      frame.rectH,
      dx,
      dy,
      dw,
      dh
    );
  }
  ctx.restore();
}

export const Home = () => {
  // -------------------- tweakables --------------------
  const CUBE_SPEED_SECONDS = 8;        // cube scroll speed
  const SFX_VOLUME = 0.05;             // 0.0 to 1.0
  const EXPLOSION_FPS = 24;            // explosion animation framerate
  const POST_EXPLOSION_DELAY_MS = 900; // wait AFTER explosion finishes, then restart cube
  const EXPLOSION_DISPLAY_W = 240;
  const EXPLOSION_DISPLAY_H = 240;
  // ---------------------------------------------------

  const cubeRef = useRef(null);

  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const sheetImgRef = useRef(null);

  const timersRef = useRef({ interval: null, timeout: null });

  const [plistData, setPlistData] = useState(null);
  const [exploding, setExploding] = useState(false);
  const [explosionAt, setExplosionAt] = useState(null); // {x,y} viewport coords
  const [cubeRunId, setCubeRunId] = useState(0);

  // Load plist + spritesheet once
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const plistUrl = new URL("./PlayerExplosion_12-uhd.plist", import.meta.url);
        const text = await fetch(plistUrl).then((r) => r.text());
        const parsed = parsePlist(text);

        const img = new Image();
        img.src = explosionSheet;
        await new Promise((res, rej) => {
          img.onload = () => res();
          img.onerror = () => rej(new Error("Failed to load explosion sheet"));
        });

        if (!alive) return;
        sheetImgRef.current = img;
        setPlistData(parsed);
      } catch (e) {
        console.error("Explosion assets failed to load:", e);
      }
    })();

    return () => {
      alive = false;
      if (timersRef.current.interval) clearInterval(timersRef.current.interval);
      if (timersRef.current.timeout) clearTimeout(timersRef.current.timeout);
    };
  }, []);

  // Prepare audio
  useEffect(() => {
    const a = new Audio(explodeSfx);
    a.preload = "auto";
    a.volume = SFX_VOLUME;
    audioRef.current = a;
  }, [SFX_VOLUME]);

  // Pause/hide cube during explosion
  const cubeStyle = useMemo(() => {
    return exploding ? { animationPlayState: "paused", opacity: 0 } : undefined;
  }, [exploding]);

  function cleanupTimers() {
    if (timersRef.current.interval) clearInterval(timersRef.current.interval);
    if (timersRef.current.timeout) clearTimeout(timersRef.current.timeout);
    timersRef.current.interval = null;
    timersRef.current.timeout = null;
  }

  function onCubeClick() {
    if (exploding) return;

    const el = cubeRef.current;
    if (!el) return;

    const r = el.getBoundingClientRect();
    const centerX = r.left + r.width / 2;
    const centerY = r.top + r.height / 2;

    cleanupTimers();

    // trigger render of explosion layer + canvas first
    setExplosionAt({ x: centerX, y: centerY });
    setExploding(true);

    // play sound immediately
    const a = audioRef.current;
    if (a) {
      a.currentTime = 0;
      a.volume = SFX_VOLUME;
      a.play().catch(() => {});
    }
  }

  // IMPORTANT: run the animation AFTER the canvas exists (after render)
  useEffect(() => {
    if (!exploding || !explosionAt) return;
    if (!plistData || !sheetImgRef.current) {
      // If assets somehow aren't ready, unstick the cube
      setExploding(false);
      setExplosionAt(null);
      setCubeRunId((v) => v + 1);
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    if (!canvas || !ctx) {
      // If canvas isn't there (shouldn't happen), unstick
      setExploding(false);
      setExplosionAt(null);
      setCubeRunId((v) => v + 1);
      return;
    }

    // Setup canvas
    const PAD = 40;
    const canvasW = EXPLOSION_DISPLAY_W + PAD * 2;
    const canvasH = EXPLOSION_DISPLAY_H + PAD * 2;
    canvas.width = canvasW;
    canvas.height = canvasH;

    const frames = plistData.frames;
    let i = 0;

    // draw first frame on next paint to be safe
    const raf = requestAnimationFrame(() => {
      drawFrame(
        ctx,
        sheetImgRef.current,
        frames[0],
        canvasW,
        canvasH,
        EXPLOSION_DISPLAY_W,
        EXPLOSION_DISPLAY_H
      );
    });

    const frameMs = Math.max(1, Math.floor(1000 / EXPLOSION_FPS));

    timersRef.current.interval = setInterval(() => {
      i += 1;

      if (i >= frames.length) {
        // end
        cleanupTimers();
        ctx.clearRect(0, 0, canvasW, canvasH);

        timersRef.current.timeout = setTimeout(() => {
          setExploding(false);
          setExplosionAt(null);
          setCubeRunId((v) => v + 1); // restart cube from left
        }, POST_EXPLOSION_DELAY_MS);

        return;
      }

      drawFrame(
        ctx,
        sheetImgRef.current,
        frames[i],
        canvasW,
        canvasH,
        EXPLOSION_DISPLAY_W,
        EXPLOSION_DISPLAY_H
      );
    }, frameMs);

    return () => {
      cancelAnimationFrame(raf);
      // Don't cleanupTimers() here blindly, because state changes will handle it.
      // But if component unmounts, outer cleanup handles it.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exploding, explosionAt, plistData, EXPLOSION_FPS, POST_EXPLOSION_DELAY_MS, EXPLOSION_DISPLAY_W, EXPLOSION_DISPLAY_H]);

  return (
    <div
      className={styles["home-page"]}
      style={{ "--cube-speed": `${CUBE_SPEED_SECONDS}s` }}
    >
      <div className={styles["content"]}>
        <img src={construction} alt="" />
        <br />
        <br />
        <iframe
          title="Fingerdash"
          width="530"
          height="300"
          src="https://www.youtube.com/embed/xPIZZIIKCJo"
        ></iframe>

        <br />
        <br />

        <button
          className={styles["ledge-button"]}
          onClick={() => window.open("https://ledgelist.netlify.app", "_blank")}
        >
          Ledge List
        </button>

        <h1 className={styles["fade-text-1"]}>My Links</h1>
        <table>
          <tbody>
            <tr className={styles["logos"]}>
              <td>
                <a href="https://youtube.com/@sdslayer100" title="My YouTube Channel!">
                  <img alt="" className={styles["logos"]} src={youtubelogo} />
                </a>
              </td>
              <td>
                <a href="https://twitter.com/sdslayer100" title="My Twitter!">
                  <img alt="" className={styles["logos"]} src={twitterlogo} />
                </a>
              </td>
              <td>
                <a href="../music" title="My Music!">
                  <img alt="" className={styles["logos"]} src={musiclogo} />
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className={styles["bottom-bar"]}>
        <img
          key={cubeRunId}
          ref={cubeRef}
          className={styles["cube"]}
          src={cube}
          alt=""
          aria-hidden="true"
          onClick={onCubeClick}
          style={cubeStyle}
        />

        <hr className={styles["bottom-hr"]} />

        <a className={styles["oldsite-link"]} href="https://oldsdslayer.netlify.app/">
          <p className={styles["oldsite-text"]}>Old, Bad Website</p>
        </a>
      </div>

      {explosionAt && (
        <div
          className={styles["explosionLayer"]}
          style={{ left: explosionAt.x, top: explosionAt.y }}
        >
          <canvas ref={canvasRef} className={styles["explosionCanvas"]} />
        </div>
      )}
    </div>
  );
};